import React, {useEffect} from 'react';
import {connect} from "react-redux";
import Loader from "./layout/loading/loader";
import {Assessment} from "@mui/icons-material";
import PageTitle from "./layout/PageTitle";
import {helper} from "../../utils/helper";
import {isAdmin, isExploitant} from "../../utils/roles";
import {usersActions} from "../../modules/users/users.actions";
import SyntheseFilter from "./synthese/SyntheseFilter";
import { Paper} from "@mui/material";
import SubmitButton from "./layout/forms/SubmitButton";
import {Formik} from "formik";
import * as Yup from 'yup';
import {synthesesActions} from "../../modules/syntheses/syntheses.actions";
import {format} from "date-fns";
import Constants from "../../utils/constants";
import {patrimoinesActions} from "../../modules/patrimoines/patrimoines.actions.js";
import Grid from "@mui/material/Grid2";
import {toast} from "react-toastify";
import {Button} from "@mui/material";
import {toastHelper} from "../../utils/toast-helper";
import {useCreateExportMutation, downloadExport, EXPORT_STATUS} from "../../modules/exports/exports.api";

// const syntheseFilterValidationSchema = Yup.object({
//     exploitant: Yup.string()
//         .required('L\'exploitant est requis'),
//     client: Yup.string()
//         .required('Le client est requis'),
//     patrimoine: Yup.string()
//         .required('Le patrimoine est requis'),
//     campagneLavage: Yup.string()
//         .required('La campagne de lavage est requise'),
//     occurence: Yup.string()
//         .required('L\'occurrence est requise'),
// });
const syntheseFilterValidationSchema = Yup.object({
    exploitant: Yup.string()
        .required('L\'exploitant est requis'),
    client: Yup.string()
        .required('Le client est requis'),
    patrimoine: Yup.string().when('bilanType', {
        is: Constants.BILAN_TYPE_BY_PATRIMOINE,
        then: (schema) => schema.required('Le patrimoine est requis'),
    }),
    conteneur: Yup.string().when(['bilanType', 'byPAV'], {
        is: (bilanType, byPAV) => bilanType === Constants.BILAN_TYPE_BY_PATRIMOINE && byPAV === true,
        then: (schema) => schema.required('Le PAV est requis'),
    }),
});

function SynthesePage({dispatch, ...props}) {
    const currentUser = helper.getCurrentUser();
    const [admin, setAdmin] = React.useState(isAdmin())
    const [exploitant, setExploitant] = React.useState(isExploitant())
    const [formComplete, isFormComplete] = React.useState(false)
    const [createExport] = useCreateExportMutation();


    useEffect(() => {
        if (admin) {
            // Fetch exploitants if the user is an admin
            if (!props.exploitants && !props.loading && !props.error) {
                dispatch(usersActions.fetchExploitants());
            }
        } else {
            // Fetch clients if the user is an exploitant
            if (!props.clients && !props.loading && !props.error) {
                dispatch(usersActions.fetchClients());
            }
        }
        // Do nothing if the user is a client
    }, [props.loading, props.error, props.exploitants, props.clients]);

    const getInitialValues = () => {
        const currentDate = new Date();
        const tomorrowDate = new Date();
        tomorrowDate.setDate(currentDate.getDate() + 1);

        return {
            exploitant: exploitant ? currentUser.id : admin ? '' : '0',
            client: (!exploitant && !admin) ? currentUser.id : '',
            bilanType: Constants.BILAN_TYPE_GLOBAL,
            patrimoine: '',
            byPAV: false,
            conteneur: '',
            dateDebut: format(currentDate, 'yyyy-MM-dd'),
            dateFin: format(tomorrowDate, 'yyyy-MM-dd'),
            // Classeur xlsx (REP-03 R10), global ou par patrimoine / PAV ; PDF en option par patrimoine
            format: Constants.SYNTHESE_FORMAT_XLSX
        }
    }

    // PDF : pipeline asynchrone « Mes exports » (POST /api/exports).
    // Le bouton ne fait plus le téléchargement, il lance le job.
    const handlePdfExport = async (values) => {
        try {
            const result = await createExport({
                type: 'synthese',
                patrimoine: Number(values.patrimoine),
                dateDebut: values.dateDebut,
                dateFin: values.dateFin,
                byPAV: Boolean(values.byPAV),
                conteneur: values.byPAV ? Number(values.conteneur) : null,
            }).unwrap();

            if (result?.statut === EXPORT_STATUS.DONE) {
                // Dédup : le même export existe déjà, téléchargeable immédiatement.
                toast.success(
                    <span>
                        Cette synthèse est déjà disponible.{' '}
                        <Button size="small" variant="outlined" color="inherit"
                                onClick={() => downloadExport(result.uuid)}>
                            Télécharger
                        </Button>
                    </span>,
                    {autoClose: false}
                );
            } else {
                toastHelper.displaySuccess("Votre synthèse est en préparation — vous la retrouverez dans Mes exports.");
            }
        } catch (e) {
            if (e?.status === 429 && e?.data?.message) {
                // Plafond d'exports simultanés : afficher le message de l'API tel quel.
                toastHelper.handleError(null, e.data.message);
            } else {
                toastHelper.handleError(e, "Oops, une erreur est survenue lors du lancement de l'export.");
            }
        }
    }

    const onPatrimoineSelected = (pId) => {
        if (pId && props.patrimoine?.id !== pId) {
            dispatch(patrimoinesActions.fetchPatrimoine(pId));
        }
    }

    return (
        <>
            <PageTitle title={"Synthèse"} icon={<Assessment/>}/>
            {props.loading &&
                <Loader/>
            }
            {!props.loading &&
                <Paper className={"synthese-content"}>
                    <Formik
                        initialValues={getInitialValues()}
                        enableReinitialize={true}
                        validationSchema={syntheseFilterValidationSchema}
                        onSubmit={async (values, {setSubmitting}) => {
                            setSubmitting(true)
                            if (values.format === 'PDF') {
                                // Le POST répond immédiatement (202) : pas de setTimeout,
                                // le bouton sort de isSubmitting à la réponse.
                                await handlePdfExport(values)
                                setSubmitting(false)
                            } else {
                                // Classeur xlsx (global, par patrimoine ou par PAV) : téléchargement direct.
                                // On attend la réponse (le classeur peut prendre plusieurs secondes) : le bouton
                                // reste en « en cours » jusqu'au fichier ou à l'erreur, plus de minuterie arbitraire.
                                await dispatch(synthesesActions.fetchSynthese(values))
                                setSubmitting(false)
                            }
                        }}
                    >
                        {({
                              values,
                              errors,
                              touched,
                              handleChange,
                              handleBlur,
                              handleSubmit,
                              isSubmitting,
                              /* and other goodies */
                          }) => (
                            <form onSubmit={handleSubmit}>

                                <SyntheseFilter isAdmin={admin} isExploitant={exploitant}
                                                exploitants={props.exploitants} clients={props.clients}
                                                values={values} onPatrimoineSelected={onPatrimoineSelected}
                                                patrimoine={props.patrimoine}
                                                errors={errors} handleChange={handleChange}/>

                                <Grid container justifyContent="center" style={{marginTop: '2rem'}}>
                                    <Grid size={4}>
                                        <SubmitButton submitting={isSubmitting} label={"Télecharger la synthèse"}/>
                                    </Grid>
                                </Grid>
                            </form>
                        )}
                    </Formik>

                </Paper>
            }
        </>
    )
}

const mapStateToProps = function (state) {
    return {
        exploitants: state.users.exploitants,
        clients: state.users.clients,
        patrimoines: state.patrimoines.patrimoines,
        patrimoine: state.patrimoines.patrimoine,
        loading: state.users.loading,
        error: state.users.err || state.patrimoines.error
    }
}

export default connect(mapStateToProps)(SynthesePage);
